import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Radio, Users, DoorOpen, IdCard } from "lucide-react";

/**
 * Public "pulse" strip showing how alive the app is — designed for the age gate
 * and auth page so first-time visitors see social proof BEFORE they log in.
 *
 * Polls /api/stats/pulse every 10s. No auth required.
 */
export default function PulseStrip({ compact = false }) {
  const [s, setS] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/stats/pulse");
        if (alive) setS(data);
      } catch {
        /* silent — this is decorative */
      }
    };
    load();
    const t = setInterval(load, 10000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  if (!s) return null;

  const items = [
    { icon: <Radio className="w-3 h-3" />, label: "LIVE NOW", value: s.live_now, accent: true, testId: "pulse-live-now" },
    { icon: <DoorOpen className="w-3 h-3" />, label: "ACTIVE ROOMS", value: s.rooms_active, testId: "pulse-active-rooms" },
    { icon: <Users className="w-3 h-3" />, label: "ROOMS TOTAL", value: s.rooms_total, testId: "pulse-rooms-total" },
    { icon: <IdCard className="w-3 h-3" />, label: "MEMBERS", value: s.lifetime_members, testId: "pulse-members" },
  ];

  return (
    <div
      className={`grid ${compact ? "grid-cols-4 gap-0" : "grid-cols-2 sm:grid-cols-4 gap-0"} border-l border-t border-white/15 max-w-2xl mx-auto`}
      data-testid="pulse-strip"
    >
      {items.map((it) => (
        <div key={it.label} className="border-r border-b border-white/15 p-3 md:p-4" data-testid={it.testId}>
          <div className="flex items-center gap-2 font-mono text-[10px] tracking-widest text-white/50">
            {it.icon}
            <span>{it.label}</span>
            {it.accent && s.live_now > 0 && (
              <span className="w-1.5 h-1.5 bg-acid animate-pulse" />
            )}
          </div>
          <div className={`mt-1 font-heading leading-none ${it.accent ? "text-3xl md:text-4xl text-acid" : "text-2xl md:text-3xl text-paper"}`}>
            {typeof it.value === "number" ? it.value.toLocaleString() : it.value}
          </div>
        </div>
      ))}
    </div>
  );
}
